/**
 * DOCUMENTATION:
 * Component: CssFlexbox
 * Purpose: Educational page detailing 1-Dimensional layouts using CSS Flexible Box Layout (Flexbox).
 * * * Note for Junior Developers:
 * - Flexbox is 1-Dimensional. It deals with either a Row OR a Column at one time.
 * - The most important concept to grasp is the Main Axis vs. the Cross Axis.
 * If your flex-direction is 'row', the Main Axis is horizontal, and the Cross Axis is vertical.
 * - Always remember: Some properties ONLY go on the Parent (Container), and some ONLY go on the Children (Items).
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function CssFlexbox() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Core Flexbox Properties
    const flexProperties = useMemo(() => [
        { property: "display: flex;", target: "Parent", description: "Activates the flex context for all its direct children." },
        { property: "flex-direction", target: "Parent", description: "Establishes the Main Axis: row (default), column, row-reverse, or column-reverse." },
        { property: "justify-content", target: "Parent", description: "Aligns items along the Main Axis (e.g., center, space-between, flex-start)." },
        { property: "align-items", target: "Parent", description: "Aligns items along the Cross Axis (e.g., center, stretch, flex-start)." },
        { property: "gap", target: "Parent", description: "Adds consistent spacing between flex items without using messy margins." },
        { property: "flex", target: "Child", description: "A shorthand for flex-grow, flex-shrink, and flex-basis. (e.g., flex: 1 1 auto;)." },
    ], []);

    // Coding examples demonstrating Flexbox
    const codingModules = useMemo(() => [
        {
            title: "1. The Holy Grail: Perfect Centering",
            code: `.container {\n  display: flex;\n  /* Main Axis Alignment (Horizontal in a row) */\n  justify-content: center;\n  /* Cross Axis Alignment (Vertical in a row) */\n  align-items: center;\n  \n  /* Give it height so we can see the vertical centering */\n  height: 100vh;\n}`,
            desc: "Before Flexbox, centering an element both vertically and horizontally required complex math and absolute positioning. Now, it takes exactly three lines of CSS on the parent container."
        },
        {
            title: "2. The Classic Navigation Bar",
            code: `.navbar {\n  display: flex;\n  /* Pushes the logo to the far left and the links to the far right */\n  justify-content: space-between;\n  align-items: center;\n  \n  padding: 1rem 2rem;\n  background-color: #100720;\n}\n\n.nav-links {\n  display: flex;\n  /* Spacing between the individual links */\n  gap: 1.5rem; \n  list-style: none;\n}`,
            desc: "Notice how we can nest Flexbox! The `.navbar` is a flex container separating the logo and the link list. The `.nav-links` list is ALSO a flex container to lay out its items horizontally."
        },
        {
            title: "3. Controlling the Children (Grow, Shrink, Basis)",
            code: `.item-standard {\n  /* flex-grow: 0 (don't grow), flex-shrink: 1 (can shrink), flex-basis: auto */\n  flex: 0 1 auto;\n}\n\n.item-hero {\n  /* flex-grow: 1 (take up all remaining space!) */\n  flex: 1 1 auto;\n}\n\n/* Example: Responsive Wrapping */\n.card-container {\n  display: flex;\n  /* If items don't fit, drop them to the next line */\n  flex-wrap: wrap; \n  gap: 20px;\n}\n.card {\n  /* Start at 300px width, but grow to fill space if available */\n  flex: 1 1 300px;\n}`,
            desc: "The `flex` property dictates how a child item behaves when there is extra space (grow) or not enough space (shrink). The `flex-basis` acts as the ideal starting width/height."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    MODERN LAYOUTS: FLEXBOX
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Thinking in One Dimension
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            Flexbox provides a more efficient way to lay out, align, and distribute space among items in a container, even when their size is unknown or dynamic. The core concept revolves around setting a <code>flex-direction</code>, which creates a <strong>Main Axis</strong>. You then use <code>justify-content</code> to align items along that axis, and <code>align-items</code> to align them on the perpendicular <strong>Cross Axis</strong>.
                        </Typography>

                        {/* ✅ Flexbox Properties Table */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Crucial Flex Properties</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Property</div>
                                    <div>Applies To</div>
                                    <div>Description</div>
                                </ResponsiveDataRow>
                            )}
                            {flexProperties.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod", fontFamily: "'Fira Code', monospace" }}>{item.property}</Box>
                                    <Box sx={{ fontSize: "0.95rem", fontWeight: 500, color: item.target === 'Parent' ? '#3ECF8E' : '#FF9F43' }}>
                                        {item.target} Container
                                    </Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/css-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
